import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { object, string } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import SearchIcon from "@mui/icons-material/Search";
import makeStyles from "@material-ui/styles/makeStyles";
import CheckIcon from '@mui/icons-material/Check';
import FormInput from "../../../components/form/FormInput";
import { updateUserDetails } from "../../../redux/actions/user";
import ConfirmModal from '../../../components/layout/ConfirmModal';
import config from "../../../config/config";

const signupSchema = object({
  firstName: string().min(1, 'First Name is required').max(30, 'First Name must be less than 30 letters'),
  lastName: string().min(1, 'Last Name is required').max(30, 'Last Name must be less than 30 letters'),
  email: string().min(1, 'Email is required').email('Email is invalid'),
  country: string(),
  city: string(),
  website: string().regex(
    /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
    'Invalid URL'
  ),
});

const containsText = (text, searchText) => text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

export default function ProfileSettings({ userId, updateUserAvatar }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const useStyles = makeStyles({
    menuItem: {
      color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f',
      fontSize: '13px',
      "&:hover": {
        color: '#009ef7',
        backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
      },
    },
    menuItemSelected: {
      color: '#fff',
      backgroundColor: '#009ef7 !important',
      alignItems: 'center',
      justifyContent: 'space-between',
      "&:hover": {
        color: '#fff',
        backgroundColor: '#009ef7 !important',
      },
    },
  });
  const styles = useStyles();

  const { clientDetailData } = useSelector(state => ({
    clientDetailData: state.user.clientDetailData,
  }));

  const defaultValues = {
    firstName: clientDetailData.firstName || "",
    lastName: clientDetailData.lastName || "",
    email: clientDetailData.email || "",
    country: clientDetailData.country || "",
    city: clientDetailData.city || "",
    website: clientDetailData.website || "",
  };
  const methods = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues,
  });

  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(clientDetailData.country || "");
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  const getCountries = async () => {
    const { data } = await fetch(config.COUNTRY_URL).then(response => response.json());
    setCountries(data);
  }
  const displayedCountries = useMemo(
  () => countries.filter((option) => containsText(option.country, searchText)),
    [searchText, countries]
  );

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    methods.setValue("firstName", clientDetailData.firstName || "");
    methods.setValue("lastName", clientDetailData.lastName || "");
    methods.setValue("email", clientDetailData.email || "");
    methods.setValue("country", clientDetailData.country || "");
    methods.setValue("city", clientDetailData.city || "");
    methods.setValue("website", clientDetailData.website || "");
    setCountry(clientDetailData.country || "");
  }, [clientDetailData]);

  const onChangeCountry = (e) => {
    setCountry(e.target.value);
    methods.setValue("country", e.target.value);
  }

  const onSubmitHandler = (data) => {
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
  }

  const onCloseConfirmModal = () => {
    setConfirmModalVisible(false);
  }

  const onSaveChanges = () => {
    onCloseModal();
    const data = {
      userId,
      ...methods.getValues(),
    };
    dispatch(updateUserDetails(data));
    updateUserAvatar();
    setTimeout(() => setConfirmModalVisible(true), 1000);
  }

  return (
    <>
      <Grid>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>Profile Settings</Typography>
      </Grid>
      <Divider sx={{ mt: 2 }} />
      <FormProvider {...methods}>
        <Box
          display='flex'
          flexDirection='column'
          component='form'
          noValidate
          autoComplete='off'
          onSubmit={methods.handleSubmit(onSubmitHandler)}>
          <Grid container alignItems="center" sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Name</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Shows on your profile and emails.</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <Grid container justifyContent='space-between' spacing={2}>
                <Grid item xs={6}>
                  <FormInput
                    placeholder='First Name'
                    type='text'
                    name='firstName'
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    placeholder='Last Name'
                    type='text'
                    name='lastName'
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Email</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Account access and booking notifications.</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                placeholder='Email'
                fullWidth
                type='email'
                name='email'
                required
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Country</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Your current country</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <Select
                fullWidth
                inputProps={{
                  sx: {
                    color: theme.palette.mode === 'light' ?
                      country ? '#5e6278' : '#bdc0ca' :
                      country ? '#fff' : '#3c3e50',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: '6px',
                    padding: '11.3px 20px',
                  }
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: '300px' } } }}
                displayEmpty={true}
                renderValue={value => value ? value : 'Select Country...'}
                sx={{
                  padding: 0,
                  '.MuiSelect-select': { paddingY: '11.3px' },
                  '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
                }}
                value={country}
                onChange={onChangeCountry}>
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Type to search..."
                    fullWidth
                    sx={{ margin: '5px 0px' }}
                    inputProps={{
                      style: {
                        paddingTop: '0px',
                        paddingBottom: '0px',
                      }
                    }}
                    InputProps={{
                      style: {
                        padding: '10px 9px',
                        fontSize: '12px',
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") {
                        // Prevents autoselecting item while typing (default Select behaviour)
                        e.stopPropagation();
                      }
                    }}
                  />
                </ListSubheader>
                {displayedCountries.map((row, index) => (
                  <MenuItem
                    key={index}
                    value={row.country}
                    selected={row.country === country}
                    classes={{ root: styles.menuItem, selected: styles.menuItemSelected }}
                  >
                    {row.country}
                    {row.country === country && <CheckIcon style={{ width: '15px', height: '15px' }} />}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>City</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Your current city</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                placeholder='City'
                fullWidth
                type='text'
                name='city'
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Website</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Website URL (if applicable)</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                placeholder='Website'
                fullWidth
                type='text'
                name='website'
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2 }} />
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              onClick={() => navigate('/superadmin')}
              size="large"
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '6px',
                padding: '11px 20px',
                "&:hover": {
                  backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212E48',
                  color: '#009ef7',
                },
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              sx={{ marginInlineStart: 1, color: '#fff', fontWeight: 600, textTransform: 'none' }}>
              Save Changes
            </Button>
          </Box>
        </Box>
      </FormProvider>
      <ConfirmModal
        visible={modalVisible}
        onClose={onCloseModal}
        symbol="!"
        title="Are you sure you want to change above settings?"
        titleYes="Yes"
        titleNo="No"
        onClickYes={onSaveChanges}
        onClickNo={onCloseModal}
      />
      <ConfirmModal
        visible={confirmModalVisible}
        onClose={onCloseConfirmModal}
        symbol="&#10004;"
        title="You have updated above settings!"
        titleNo="OK"
        onClickNo={onCloseConfirmModal}
      />
    </>
  );
}
